import React, { useState } from "react";
import { Link } from "react-router-dom";
import Pulse from "../Loader/Pulse";

export default function TabbedBadgeList({ tabs }) {
  const [activeTab, setActiveTab] = useState(0);
  const activeTabData = tabs[activeTab];
  const { linkTo, columns, data } = activeTabData;
  const isLoading = data === null || data === undefined;
  const safeData = Array.isArray(data) ? data : [];
  const TOTAL_ROWS = 5;
  const emptyRowsCount = Math.max(0, TOTAL_ROWS - safeData.length);

  return (
    <>
      <style>{`
        .tabbed-list-container {
          overflow: hidden;
          display: flex;
          flex-direction: column;
          width: 100%;
        }

        /* ===== TABS HEADER ===== */
        .snap-data-header-tabs {
          display: flex;
          width: 100%;
          background-color: var(--white);
        }

        .tab-header-item {
          flex: 1;
          cursor: pointer;
          padding: 15px;
          display: flex;
          flex-direction: column;
          row-gap: 2px;
          opacity: 0.4;
          border-radius: 0 0 5px 5px;
          background-color: transparent; 
          border-bottom: 3px solid var(--border-color);
          transition: all 0.3s ease-in-out;
        }

        .tab-header-item:first-child {
          border-right: 1px solid var(--border-color);
        }

        .tab-header-item.active {
          opacity: 1;
          background-color: transparent;
          border-radius: 0 0 5px 5px;
          border-bottom: 3px solid var(--primary);
        }

        .tab-value {
          font-size: 24px;
          font-weight: 500;
          color: var(--font-primary);
        }

        /* ===== BADGE LIST BODY ===== */
        .snap-data-body {
          width: 100%;
          padding: 12px 0;
        }

        .snap-data-link {
          text-decoration: none;
          color: inherit;
          display: flex;
          flex-direction: column;
          gap: 6px;
          justify-content: space-between;
          padding: 0 4px;
          min-height: 315px;
        }

        /* Loader Styles */
        .cp__watchlist__loading {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 3rem 0;
          color: var(--font-secondary);
          font-size: 12px;
          font-weight: 500;
        }
        .cp__watchlist__loading p {
          margin-top: 10px;
        }

        /* Badge Row Container */
        .badge-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 14px 14px;
          border-radius: 8px; 
          transition: transform 0.2s ease-in;
          align-items: flex-start;
        }

        .badge-row:hover {
          transform: translateY(-1px);
        }

        /* Alternating Backgrounds */
        .badge-row.data-row:nth-child(odd) {
          box-shadow: 0 2px 6px 1px var(--primary20);
          border-left: 3px solid var(--primary);
        }
        
        .badge-row.data-row:nth-child(even) {
          box-shadow: 0 2px 8px 1px var(--secondary20);
          border-left: 3px solid var(--secondary);
        }

        /* Data Cells inside Badge */
        .badge-cell {
          font-size: 12px;
          font-weight: 500;
          color: var(--font-primary);
          flex: 1;
          // max-width: 60ch;       
          // white-space: nowrap;   
          // overflow: hidden;      
          // text-overflow: ellipsis;
        }

        /* Alignments */
        .badge-cell:first-child {
          text-align: left;
          flex: 0 0 65%;
        }
        .badge-cell:nth-child(2) {
          text-align: right;
          flex: 0 0 35%;
        }

        .badge-cell.mf-name-cell {
          flex: 0 0 78%;
        }
        .badge-cell.mf-value-cell {
          flex: 0 0 22%;
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          justify-content: center;
        }

        .badge-cell:last-child {
          text-align: right;
        }
        .badge-cell:not(:first-child):not(:last-child) {
          text-align: center;
        }

        /* ===== EMPTY STATES ===== */
        .badge-row.no-data {
          background-color: var(--base-200);
          justify-content: center;
          color: var(--font-secondary);
          font-weight: 500;
          font-size: 13px;
        }

        .badge-row.empty-placeholder {
          background-color: transparent;
          border: 1px dashed var(--border-color);
          opacity: 0.5;
        }
      `}</style>

      <div className="tabbed-list-container">
        <div className="snap-data-header-tabs">
          {tabs.map((tab, index) => (
            <div
              key={tab.id || index}
              onClick={() => setActiveTab(index)}
              className={`tab-header-item ${activeTab === index ? "active" : ""}`}
            >
              <div className="tab-title">{tab.title}</div>
              <div className="tab-value">{tab.value}</div>
              <div className="tab-subtext">{tab.subtext}</div>
            </div>
          ))}
        </div>

        <div className="snap-data-body">
          <Link to={linkTo} className="snap-data-link">
            {isLoading ? (
              <div className="cp__watchlist__loading">
                <Pulse />
                {/* <p>Loading Data...</p> */}
              </div>
            ) : safeData.length === 0 ? (
              // NO DATA STATE 
              <>
                <div className="badge-row no-data">No data found</div>
                {Array.from({ length: TOTAL_ROWS - 1 }).map((_, idx) => (
                  <div
                    key={`empty-nodata-${idx}`}
                    className="badge-row empty-placeholder"
                  >
                    <div className="badge-cell">&nbsp;</div>
                  </div>
                ))}
              </>
            ) : (
              // DATA STATE
              <>
                {safeData.map((row, rowIndex) => (
                  <div key={rowIndex} className="badge-row data-row">
                    {(columns || []).map((col, colIndex) => {
                      const cellValue =
                        row[col.toLowerCase()] ?? row[col] ?? "-";
                      const isValueColumn = col.toLowerCase() === "value";
                      const hasTotalReturns =
                        row.totalReturns !== undefined &&
                        row.totalReturns !== null;
                      const isPositive =
                        hasTotalReturns && Number(row.totalReturns) > 0;
                      const isNegative =
                        hasTotalReturns && Number(row.totalReturns) < 0;
                      const valueClass = isPositive
                        ? "positive"
                        : isNegative
                          ? "negative"
                          : "";

                      const isMfTab = activeTabData.id === "mutual_funds";

                      return (
                        <div key={colIndex} className={`badge-cell ${isMfTab && colIndex === 0 ? "mf-name-cell" : ""} ${isMfTab && colIndex === 1 ? "mf-value-cell" : ""}`}>
                          {isMfTab && colIndex === 1 ? (
                            <>
                              <span>{cellValue}</span>
                              <span
                                className={`${valueClass}`}
                                style={{ fontSize: "10px", marginTop: "2px" }}
                              >
                                {isValueColumn && hasTotalReturns
                                  ? `( ${row.totalReturns}% )`
                                  : ""}
                              </span>
                            </>
                          ) : (
                            <>
                              {cellValue} &nbsp;
                              <span className={`${valueClass}`}>
                                {isValueColumn && hasTotalReturns
                                  ? ` ( ${row.totalReturns}% )`
                                  : ""}
                              </span>
                            </>
                          )}
                          {/* Render StockName under the very first column */}
                          {colIndex === 0 && row.StockName && (
                            <div
                              style={{
                                marginTop: "2px",
                                color: "var(--font-secondary)",
                                fontSize: "10px",
                                fontWeight: "500",
                              }}
                            >
                              {row.StockName}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ))}
                {Array.from({ length: emptyRowsCount }).map((_, idx) => (
                  <div
                    key={`empty-${idx}`}
                    className="badge-row empty-placeholder"
                  >
                    <div className="badge-cell">&nbsp;</div>
                  </div>
                ))}
              </>
            )}
          </Link>
        </div>
      </div>
    </>
  );
}